/* External */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import getL from 'lodash-es/get';

/* Ott-common */
import { assetTypes } from '@dtvgo/rtk-query-api';

/* Components */
import Button from '../button/Button';
import PdpPlayButton from '../pdpPlayButton/PdpPlayButton';
import AddOrRemoveFromMyListButton from '../addOrRemoveFromMyListButton/AddOrRemoveFromMyListButton';

/* Styles */
import './PdpButtons.scss';

/* Other */
import {
  NO_ENTITLEMENT_URL, redirectToApp, redirectToPlansPage, redirectToLoginPage,
  PLAY_SOURCE_PAGE,
} from '../../utils';
import { history } from '../../state/configureStore';
import { useIsResponsive } from '../../hooks/useIsResponsive';

function PdpButtons({
  assetId, vmsId, showMyListButton, assetToPlayId, country, assetType,
  title, isLive, channelId, rating, actionOrigin = '',
  shouldDisplayLockIcon,
  duration, progress,
  pdpPlayButtonText, pdpPlayButtonIcon, isPublic,
  intl: { formatMessage },
  userCountry,
}) {
  let buttonContent;
  const isMobile = useIsResponsive('(max-width: 1024px)');
  const storeCode = (userCountry || country || '').toLowerCase();

  const pdpPlayButton = (
    <PdpPlayButton
      assetProps={{
        asset: {
          assetId: assetToPlayId,
          mainAssetId: assetId,
          playSource: PLAY_SOURCE_PAGE.PDP,
          actionOrigin: actionOrigin || '',
          type: assetType,
          channelId,
          rating,
          vmsId,
        },
        isLive,
      }}
      duration={duration}
      progress={progress}
      text={pdpPlayButtonText}
      icon={pdpPlayButtonIcon}
    />
  );

  const addToList = (
    <AddOrRemoveFromMyListButton
      assetId={assetId}
      title={title}
      type={assetType === assetTypes.episode ? assetTypes.serie : assetType}
      assetToPlayId={assetToPlayId}
      actionOrigin={actionOrigin || ''}
    />
  );

  // Not logged in users - show "See plans" and "Already have an account" buttons
  if (assetToPlayId && isPublic) {
    buttonContent = (
      <>
        <Button
          icon="dtv-icon-layers"
          onClick={() => {
            redirectToPlansPage(storeCode);
          }}
        >
          {formatMessage({
            id: 'public.pdp.seePlans',
            defaultMessage: 'Conocer planes',
          })}
        </Button>
        <Button
          icon="dtv-icon-user"
          variant="secondary"
          onClick={() => {
            redirectToLoginPage(storeCode);
          }}
        >
          {formatMessage({ id: 'public.pdp.alreadyHaveAccount', defaultMessage: 'Ya tengo cuenta' })}
        </Button>
      </>
    );
  }

  // Logged in users but have no access to the content - show button "Wanna watch" with a lock icon
  // If mobile, it will redirect to No Entitlement page, if desktop it will redirect to player
  if (assetToPlayId && assetId && !isPublic && shouldDisplayLockIcon) {
    buttonContent = isMobile ? (
      <Button
        icon="dtv-icon-lock"
        onClick={() => {
          history.push(`${NO_ENTITLEMENT_URL}/${assetType === assetTypes.episode ? assetId : assetToPlayId}`);
        }}
      >
        {formatMessage({
          id: 'public.pdp.playButton',
          defaultMessage: 'Quiero verlo',
        })}
      </Button>
    ) : (
      <>
        {pdpPlayButton}
        {showMyListButton && assetId && !isMobile && addToList}
      </>
    );
  }

  // Logged in users with access to the content
  // If mobile, it will show a "Watch on App" button, redirecting the user to the App.
  // If desktop, it will open the default player
  if (assetToPlayId && assetId && !isPublic && !shouldDisplayLockIcon) {
    buttonContent = (
      <>
        {isMobile ? (
          <Button
            icon={pdpPlayButtonIcon}
            onClick={() => {
              redirectToApp(storeCode);
            }}
          >
            {formatMessage({ id: 'pdp.watchAtApp', defaultMessage: 'Ver en la app DGO' })}
          </Button>
        ) : pdpPlayButton}
        {showMyListButton && assetId && !isMobile && addToList}
      </>
    );
  }

  return (
    <div className="dtv-pdp-buttons">
      {buttonContent}
    </div>
  );
}

function mapStateToProps({ global }) {
  const { userCountry } = getL(global, 'configurationAEM.data', '');
  return {
    userCountry,
  };
}

PdpButtons.propTypes = {
  assetId: PropTypes.string,
  assetToPlayId: PropTypes.string,
  assetType: PropTypes.string,
  shouldDisplayLockIcon: PropTypes.bool,
  pdpPlayButtonText: PropTypes.string,
  pdpPlayButtonIcon: PropTypes.string,
  showMyListButton: PropTypes.bool,
  isPublic: PropTypes.bool,
  country: PropTypes.string,
  title: PropTypes.string,
  isLive: PropTypes.bool,
  channelId: PropTypes.string,
  rating: PropTypes.string,
  duration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  progress: PropTypes.number,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
};

PdpButtons.defaultProps = {
  assetId: '',
  assetToPlayId: '',
  assetType: '',
  pdpPlayButtonText: '',
  pdpPlayButtonIcon: '',
  showMyListButton: false,
  shouldDisplayLockIcon: false,
  isPublic: false,
  country: '',
  title: '',
  isLive: false,
  channelId: '',
  rating: '',
  duration: '',
  progress: 0,
};

export default connect(mapStateToProps)(injectIntl(PdpButtons));
